import { create } from "zustand";

// Post-reset hand-off (designer requirement: the email is prefilled on the
// sign-in screen after completing a password reset). The reset action page
// and the next sign-in are SEPARATE page loads bridged by real navigation
// (login page → app URL → IAP → login page), so an in-memory value cannot
// survive; sessionStorage does — same-tab only, gone when the tab closes.
// Deliberately NOT localStorage: remembering emails across visits is the
// browser's job (autocomplete), and the Hosting site is shared by all brands.
const RESET_EMAIL_KEY = "lernwelt-login:reset-email";

/** Called after a confirmed password reset with the verified account email. */
export function stashResetEmail(email: string): void {
  try {
    sessionStorage.setItem(RESET_EMAIL_KEY, email);
  } catch {
    // Storage unavailable (embedded/private contexts) — prefill is best-effort.
  }
}

/** Called after a successful sign-in: the hand-off is done. Read-then-clear
 *  at load time would be fragile — IAP/gcip-iap may load this page more than
 *  once (e.g. selectAuthSession hops) before the form ever renders. */
export function clearStashedEmail(): void {
  try {
    sessionStorage.removeItem(RESET_EMAIL_KEY);
  } catch {
    // Ignore — worst case the prefill shows again on the next sign-in.
  }
}

function peekStashedEmail(): string {
  try {
    return sessionStorage.getItem(RESET_EMAIL_KEY) ?? "";
  } catch {
    return "";
  }
}

type LastEmailState = {
  email: string;
  setEmail: (email: string) => void;
};

/**
 * Email carry-over within the auth flow: reset-password prefills with the
 * address already typed on sign-in (and back) — the same hand-off
 * FirebaseUI's password recovery does. Seeded from the one-shot post-reset
 * stash (see above); beyond that it lives in memory only.
 */
export const useLastEmailStore = create<LastEmailState>()((set) => ({
  email: peekStashedEmail(),
  setEmail: (email) => set({ email }),
}));
